import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {StabilityRuleFormSchema} from '@/modules/drug-catalog/stability-rules/schemas'
import {elementToArray, getFirstElementOrSelf} from '@cypro/ui/utils'
import {AddStabilityRuleSchema} from '@cypro/engine/mutators'
import {BasicDataResources, StabilityRule, StorageLabel} from '@cypro/engine/schema'

export function getStabilityRuleFormDefaultValues(rule: StabilityRule | undefined): StabilityRuleFormSchema {
  return {
    id: rule?.id,
    stabilityStage: elementToArray(rule?.stabilityStage),
    startEventNotes: rule?.startEventNotes || '',

    carrierDrugId: elementToArray(rule?.carrierDrugId),
    additionalActiveSubstanceDrugId: elementToArray(rule?.additionalActiveSubstanceDrugId),

    storageLabelId: elementToArray(rule?.storageLabelId),
    tempMinC: rule?.tempMinC ?? undefined,
    tempMaxC: rule?.tempMaxC ?? undefined,
    lightProtectionRequired: Boolean(rule?.lightProtectionRequired),
    moistureProtectionRequired: Boolean(rule?.moistureProtectionRequired),
    doNotRefrigerate: Boolean(rule?.doNotRefrigerate),
    doNotFreeze: Boolean(rule?.doNotFreeze),
    controlledTemperature: Boolean(rule?.controlledTemperature),
    keepInOriginalPackaging: Boolean(rule?.keepInOriginalPackaging),

    durationValue: rule?.durationValue || 0,
    durationUnit: elementToArray(rule?.durationUnit),
    expiryRuleType: elementToArray(rule?.expiryRuleType),
    microbiologicalLimitation: elementToArray(rule?.microbiologicalLimitation),
    discardRemainderRequired: Boolean(rule?.discardRemainderRequired),

    containerTypeId: elementToArray(rule?.containerTypeId),
    containerMaterialsIds: rule?.containerMaterials?.map(item => item.containerMaterialId) || [],
    singleDoseOnly: Boolean(rule?.singleDoseOnly),
    preservativePresent: Boolean(rule?.preservativePresent),
    requiresAsepticHandling: Boolean(rule?.requiresAsepticHandling),
    compatibilityNotes: rule?.compatibilityNotes || '',

    notes: rule?.notes || '',

    labelCopies: rule?.labelCopies ?? undefined,

    defaultRule: Boolean(rule?.isDefaultStage),

    source: rule?.source,
  }
}

export function mapStabilityRuleFormToApiValues(rule: StabilityRuleFormSchema): AddStabilityRuleSchema {
  return {
    ...rule,
    stabilityStage: getFirstElementOrSelf(rule.stabilityStage),
    storageLabelId: getFirstElementOrSelf(rule.storageLabelId),
    durationUnit: getFirstElementOrSelf(rule.durationUnit),
    expiryRuleType: getFirstElementOrSelf(rule.expiryRuleType),
    microbiologicalLimitation: getFirstElementOrSelf(rule.microbiologicalLimitation),
    containerTypeId: getFirstElementOrSelf(rule.containerTypeId),
    carrierDrugId: getFirstElementOrSelf(rule.carrierDrugId),
    additionalActiveSubstanceDrugId: getFirstElementOrSelf(rule.additionalActiveSubstanceDrugId),
  }
}

export function getStabilityRuleStorageLabel(
  rule: StabilityRule | StabilityRuleFormSchema,
  storageLabels: StorageLabel[]
) {
  if ('storageLabel' in rule) {
    return rule.storageLabel?.name
  }

  if ('storageLabelId' in rule && rule.storageLabelId) {
    const id = rule.storageLabelId ? getFirstElementOrSelf(rule.storageLabelId) : null
    if (!id) return ''
    return storageLabels?.find(label => label.id === id)?.name
  }

  return ''
}

export function useStabilityRuleStorageLabel(rule: StabilityRule | StabilityRuleFormSchema) {
  const {items: storageLabels} = useGetBasicDataItems({resource: BasicDataResources.storageLabels})
  return getStabilityRuleStorageLabel(rule, storageLabels)
}
